import { LocaleText } from '@/types/locales'
import { createContext, ReactNode, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { getLocaleFromLocaleString, getLocaleStringFromLocale, LocaleConstants, LocaleStrings } from '@lib-helpers'
import { usePathname, useSearchParams } from 'next/navigation'

const DEFAULT_FALLBACK_LOCALE: LocaleConstants = LocaleConstants.NL
const DEFAULT_FALLBACK_LOCALE_STRING: LocaleStrings = LocaleStrings.NL
const AVAILABLE_LOCALES: LocaleConstants[] = [DEFAULT_FALLBACK_LOCALE, LocaleConstants.EN]

const dayjsLocales: { [key: string]: Promise<ILocale> } = {
    [LocaleConstants.NL]: import('dayjs/locale/nl'),
    [LocaleConstants.EN]: import('dayjs/locale/en'),
}

const getDayjsLocaleTranslations = (locale: LocaleConstants): Promise<ILocale> => {
    if (!AVAILABLE_LOCALES.includes(locale)) {
        return dayjsLocales[DEFAULT_FALLBACK_LOCALE]
    }

    return dayjsLocales[locale]
}

interface LocaleProviderProps {
    children: ReactNode
    defaultLocale: LocaleStrings
}

interface LocaleProviderValue {
    activeLocale: LocaleConstants
    activeLocaleString: LocaleStrings
    defaultLocale: LocaleConstants
    getLocaleText?: (text: LocaleText, locale?: LocaleConstants) => string
    setLocale?: (locale: LocaleConstants) => void
}

export const LocaleContext = createContext<LocaleProviderValue>({
    activeLocale: DEFAULT_FALLBACK_LOCALE,
    activeLocaleString: DEFAULT_FALLBACK_LOCALE_STRING,
    defaultLocale: DEFAULT_FALLBACK_LOCALE,
})

export const LocaleProvider = ({ children, defaultLocale }: LocaleProviderProps) => {
    const [activeLocale, setActiveLocale] = useState<LocaleConstants>(getLocaleFromLocaleString(defaultLocale))
    const pathname = usePathname()
    const searchParams = useSearchParams()

    useEffect(() => {
        getDayjsLocaleTranslations(activeLocale).then(() => {
            dayjs.locale(getLocaleStringFromLocale(activeLocale))
        })

        // Pushes the current route but with the new locale
        const newUrl = `${['', getLocaleStringFromLocale(activeLocale), ...pathname.split('/').slice(2)].join('/')}${
            searchParams.toString() ? '?' + searchParams.toString() : ''
        }`

        // Update URL without navigation (no page refresh)
        if (typeof window !== 'undefined' && window.location.pathname + (window.location.search || '') !== newUrl) {
            window.history.replaceState(null, '', newUrl)
        }
    }, [activeLocale])

    const getLocaleText = (text: LocaleText, locale?: LocaleConstants): string => {
        if (locale && text[getLocaleStringFromLocale(locale)]) return text[getLocaleStringFromLocale(locale)]
        if (text[getLocaleStringFromLocale(activeLocale)]) return text[getLocaleStringFromLocale(activeLocale)]
        if (text[getLocaleStringFromLocale(DEFAULT_FALLBACK_LOCALE)])
            return text[getLocaleStringFromLocale(DEFAULT_FALLBACK_LOCALE)]
        return text[Object.keys(text)[0]]
    }

    const setLocale = (locale: LocaleConstants): void => {
        if (locale !== activeLocale) setActiveLocale(locale)
    }

    const getActiveLocalesString = (): LocaleStrings => {
        return activeLocale === LocaleConstants.EN ? LocaleStrings.EN : LocaleStrings.NL
    }

    return (
        <LocaleContext.Provider
            value={{
                activeLocale,
                activeLocaleString: getActiveLocalesString(),
                defaultLocale: DEFAULT_FALLBACK_LOCALE,
                getLocaleText,
                setLocale,
            }}
        >
            {children}
        </LocaleContext.Provider>
    )
}
